import * as React from 'react';
import { useMemo } from 'react';
import { History } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { HistoryEntry } from '@klareospace/pharos-lib-types';
import {
  Button,
  ErrorBanner,
  HistoryFeed,
  ORDER_STATUS_BADGE_COLORS,
  type HistoryFeedDiffOperationLabels,
} from '@klareospace/pharos-lib-web-utils';

import { resolveForeignOrderStatusLabel } from '&/features/orders';

import { mapHistoryEntries } from '../utils/history-mapper';
import { TaskDetailSection } from './task-detail-section';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TaskHistorySectionProps {
  readonly history: HistoryEntry[] | undefined;
  readonly isError: boolean;
  readonly isLoading: boolean;
  readonly onRetry: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * "History" section in `TaskDetailDialog`, rendered above the Proofs section — the shared
 * `HistoryFeed` fed with the task's lifecycle events, most-recent-first (server-sorted). Read-only.
 */
export function TaskHistorySection({
  history,
  isError,
  isLoading,
  onRetry,
}: TaskHistorySectionProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('tasks');

  const entries = useMemo(
    () => (history === undefined ? [] : mapHistoryEntries(history, { t, tCommon })),
    [history, t, tCommon],
  );

  const diffOperationLabels: HistoryFeedDiffOperationLabels = {
    added: tCommon('history.diff.added'),
    removed: tCommon('history.diff.removed'),
    updated: tCommon('history.diff.updated'),
  };

  return (
    <TaskDetailSection icon={History} title={tCommon('fields.historyTitle')}>
      {isError ? (
        <div className="flex flex-col items-start gap-2">
          <ErrorBanner message={t('history.error')} />
          <Button onClick={onRetry} size="sm" variant="outline">
            {tCommon('errors.retry')}
          </Button>
        </div>
      ) : (
        <HistoryFeed
          diffOperationLabels={diffOperationLabels}
          emptyMessage={t('history.empty')}
          entries={entries}
          formatters={{
            formatStatus: (status: string): string => resolveForeignOrderStatusLabel(status, t, tCommon),
            statusBadgeColors: ORDER_STATUS_BADGE_COLORS,
          }}
          isLoading={isLoading || history === undefined}
        />
      )}
    </TaskDetailSection>
  );
}
